/**
 * ConfirmDialog.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * A dynamic, reusable confirmation dialog.
 *
 * Props:
 *   open        {boolean}          — controls visibility
 *   onClose     {function}         — fires when "No" is clicked or dialog is dismissed
 *   onConfirm   {function}         — fires when "Yes" is clicked
 *   title       {string|ReactNode} — dialog header title  (default: 'Confirm Action')
 *   content     {string|ReactNode} — optional body content / description
 *   titleIcon   {ReactNode}        — optional lucide-react icon shown beside title
 *   confirmText {string}           — label for the confirm button (default: 'Yes')
 *   cancelText  {string}           — label for the cancel button  (default: 'No')
 *   confirmColor {string}          — MUI color for confirm btn    (default: 'error')
 *   loading     {boolean}          — shows spinner on confirm btn when true
 *   maxWidth    {string}           — 'xs'|'sm'|'md' (default 'xs')
 *
 * Usage:
 *   <ConfirmDialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onConfirm={handleDelete}
 *     title="Delete Record"
 *     content="Are you sure you want to delete this record? This action cannot be undone."
 *     titleIcon={<Trash2 size={18} />}
 *     confirmText="Delete"
 *     confirmColor="error"
 *   />
 */

import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  IconButton,
  Typography,
  Slide,
  Box,
  CircularProgress
} from '@mui/material';
import { X, AlertTriangle } from 'lucide-react';

/* ── Slide-up transition ─────────────────────────────────────────────────── */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Shake keyframe (injected once) ─────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('__cfd_shake__')) {
  const style = document.createElement('style');
  style.id = '__cfd_shake__';
  style.innerHTML = `
    @keyframes __cfd_shake {
      0%,100% { transform: translateX(0); }
      20%      { transform: translateX(-6px); }
      40%      { transform: translateX(6px);  }
      60%      { transform: translateX(-4px); }
      80%      { transform: translateX(4px);  }
    }
    .cfd-shake-anim { animation: __cfd_shake 0.38s ease; }
  `;
  document.head.appendChild(style);
}

/* ════════════════════════════════════════════════════════════════════════════
   ConfirmDialog
   ════════════════════════════════════════════════════════════════════════════ */
const ConfirmDialog = ({
  open = false,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  content = null,
  titleIcon = null,
  confirmText = 'Yes',
  cancelText = 'No',
  confirmColor = 'error',
  loading = false,
  maxWidth = 'xs'
}) => {
  const paperRef = useRef(null);
  const [shaking, setShaking] = useState(false);

  const handleBackdropClose = (_event, reason) => {
    if (loading) {
      if (!shaking) {
        setShaking(true);
        setTimeout(() => setShaking(false), 400);
      }
      return;
    }
    onClose?.(_event, reason);
  };

  const handleNo = () => {
    if (loading) return;
    onClose?.();
  };

  const handleYes = () => {
    onConfirm?.();
  };

  /* Resolve icon — fall back to a warning triangle */
  const resolvedIcon = titleIcon ?? <AlertTriangle size={18} />;

  /* Resolve icon background color based on confirmColor */
  const iconBgMap = {
    error:   'rgba(239,68,68,0.12)',
    warning: 'rgba(245,158,11,0.12)',
    success: 'rgba(34,197,94,0.12)',
    primary: 'rgba(59,130,246,0.12)',
    info:    'rgba(99,102,241,0.12)'
  };
  const iconColorMap = {
    error:   '#ef4444',
    warning: '#f59e0b',
    success: '#22c55e',
    primary: '#3b82f6',
    info:    '#6366f1'
  };
  const iconBg    = iconBgMap[confirmColor]    ?? iconBgMap.error;
  const iconColor = iconColorMap[confirmColor] ?? iconColorMap.error;

  return (
    <Dialog
      open={open}
      onClose={handleBackdropClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        ref: paperRef,
        className: shaking ? 'cfd-shake-anim' : undefined,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)'
        }
      }}
      slots={{ transition: SlideUp }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.75,
          bgcolor: '#fafafa',
          gap: 1.5
        }}
      >
        {/* Left side: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: iconBg,
              color: iconColor,
              flexShrink: 0
            }}
          >
            {resolvedIcon}
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={700}
            fontSize="0.97rem"
            color="text.primary"
            letterSpacing="-0.01em"
            noWrap
          >
            {title}
          </Typography>
        </Box>

        {/* Close (X) button */}
        <IconButton
          size="small"
          onClick={handleNo}
          aria-label="Close dialog"
          disabled={loading}
          sx={{
            color: 'text.secondary',
            bgcolor: 'grey.100',
            borderRadius: 2,
            flexShrink: 0,
            '&:hover': { bgcolor: '#fee2e2', color: 'error.main' },
            transition: 'all 0.15s ease'
          }}
        >
          <X size={16} />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Body ───────────────────────────────────────────────────────── */}
      {content && (
        <>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            {typeof content === 'string' ? (
              <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                {content}
              </Typography>
            ) : (
              content
            )}
          </DialogContent>
          <Divider />
        </>
      )}

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          gap: 1,
          bgcolor: '#fafafa',
          justifyContent: 'flex-end'
        }}
      >
        {/* No / Cancel */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleNo}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.82rem',
            borderRadius: 1.5,
            px: 2,
            py: 0.6,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': { borderColor: 'text.primary', color: 'text.primary', bgcolor: 'grey.50' },
            transition: 'all 0.15s ease'
          }}
        >
          {cancelText}
        </Button>

        {/* Yes / Confirm */}
        <Button
          variant="contained"
          size="small"
          color={confirmColor}
          onClick={handleYes}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={13} color="inherit" sx={{ mr: 0.5 }} /> : null
          }
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.82rem',
            borderRadius: 1.5,
            px: 2,
            py: 0.6,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
            transition: 'all 0.15s ease'
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
